import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import { ControlItem } from '../../../interfaces/interface';
import { ESG_INDICATORS } from '../../../shared/enums/enums';
import { BulkGroupItem } from '../control-panel/pricing-management/bulk-pricing/bulk-group-mgmt/bulk-group-interface';
import { Issuer, PotentialIssuer, RatingAgencies, Sector } from '../issuer-interface';
import {
  AddIssuers,
  FetchCountries,
  FetchCurrencies,
  FetchIssuers,
  FetchLookupCodes,
  FetchRatings,
  FetchSectors,
  FetchSelectedIssuerCurrencies,
  SetBulkNoteOverrideFlag,
  SetSelectedEntity,
  SetSelectedPricingSource,
  ToggleEditMode,
  TogglePricingMode,
  UpdateIssuerIsPreviouslyPriced
} from './issuer-actions';

export interface IssuerStoreModel {
  isBulkPricingMode: boolean;
  isEditMode: boolean;
  isBulkNoteOverrideEnabled: boolean;
  selectedEntity: PotentialIssuer | BulkGroupItem;
  countries: ControlItem[];
  sectors: Sector[];
  corpTickers: ControlItem[];
  esg: ControlItem[];
  fairValueDrivers: ControlItem[];
  yieldFlags: ControlItem[];
  pricingSources: ControlItem[];
  selectedPricingSource: ControlItem;
  productTypes: ControlItem[];
  currencies: ControlItem[];
  ratings: RatingAgencies;
  issuers: Issuer[];
  issuersMap: Map<number, Issuer>;
  selectedIssuerCurrencies: ControlItem[];
}

@State<IssuerStoreModel>({
  name: 'issuerState',
  defaults: {
    isBulkPricingMode: false,
    isEditMode: false,
    isBulkNoteOverrideEnabled: true,
    selectedEntity: null,
    countries: [],
    esg: [],
    corpTickers: [],
    productTypes: [],
    fairValueDrivers: [],
    yieldFlags: [],
    pricingSources: [],
    selectedPricingSource: null,
    currencies: [],
    ratings: null,
    sectors: [],
    issuers: [],
    issuersMap: new Map(),
    selectedIssuerCurrencies: []
  }
})

@Injectable()
export class IssuerState {
  http = inject(HttpClient);

  @Action(TogglePricingMode)
  togglePricingMode({ patchState, getState }: StateContext<IssuerStoreModel>, { isBulk }: TogglePricingMode): void {
    const isBulkPricingMode = isBulk !== undefined ? isBulk : !getState().isBulkPricingMode;
    patchState({
      isBulkPricingMode,
      selectedEntity: null,
      selectedPricingSource: null,
      isEditMode: false
    })
  }

  @Action(ToggleEditMode)
  toggleEditMode({ patchState, getState }: StateContext<IssuerStoreModel>, { isEdit }: ToggleEditMode): void {
    const isEditMode = isEdit !== undefined ? isEdit : !getState().isEditMode;
    patchState({ isEditMode })
  }

  @Action(SetSelectedEntity)
  setSelectedEntity({ patchState }: StateContext<IssuerStoreModel>, { selectedEntity }: SetSelectedEntity): void {
    if (!selectedEntity) {
      patchState({ selectedEntity, isEditMode: false });
    } else {
      patchState({ selectedEntity });
    }
  }

  @Action(SetSelectedPricingSource)
  setSelectedPricingSource({ patchState }: StateContext<IssuerStoreModel>, { selectedPricingSource }: SetSelectedPricingSource): void {
    patchState({ selectedPricingSource })
  }

  @Action(SetBulkNoteOverrideFlag)
  setBulkNoteOverrideFlag({ patchState }: StateContext<IssuerStoreModel>, { isBulkNoteOverrideEnabled }: SetBulkNoteOverrideFlag): void {
    patchState({ isBulkNoteOverrideEnabled })
  }

  @Action(AddIssuers)
  addIssuers({ patchState }: StateContext<IssuerStoreModel>, { issuers }: AddIssuers): void {
    const issuersMap = new Map<number, Issuer>();
    const corpTickers = [];
    issuers.forEach(i => {
      issuersMap.set(i.issuerId, i);
      if (i.originalTicker) {
        corpTickers.push({ id: i.issuerId, label: i.originalTicker });
      }
    })
    patchState({ issuers, issuersMap, corpTickers })
  }

  @Action(FetchCountries)
  fetchCountries({ patchState }: StateContext<IssuerStoreModel>): Observable<ControlItem[]> {
    return this.http.get<ControlItem[]>(`service/countries`).pipe(
      tap(countries => patchState({ countries }))
    );
  }

  @Action(FetchIssuers)
  fetchIssuers({ dispatch }: StateContext<IssuerStoreModel>): Observable<Issuer[]> {
    return this.http.get<Issuer[]>(`service/issuers`).pipe(
      tap(issuers => dispatch(new AddIssuers(issuers)))
    );
  }

  @Action(FetchLookupCodes)
  fetchLookupCodes({ patchState }: StateContext<IssuerStoreModel>): Observable<any> {
    return this.http.get<any>(`service/lookupCodes`).pipe(
      tap(lookupCodes => patchState({
        esg: lookupCodes.esgIndicator.map(item => ({ ...item, icon: ESG_INDICATORS[item.id] })),
        productTypes: lookupCodes.productCategory,
        fairValueDrivers: lookupCodes.fairValueDriver,
        yieldFlags: lookupCodes.yieldFlag,
        pricingSources: lookupCodes.pricingSource,
      }))
    );
  }

  @Action(FetchSectors)
  fetchSectors({ patchState }: StateContext<IssuerStoreModel>): Observable<Sector[]> {
    return this.http.get<Sector[]>(`service/sectors`).pipe(
      tap(sectors => patchState({ sectors }))
    );
  }

  @Action(FetchRatings)
  fetchRatings({ patchState }: StateContext<IssuerStoreModel>): Observable<RatingAgencies> {
    return this.http.get<RatingAgencies>(`service/ratings`).pipe(
      tap(ratings => patchState({ ratings }))
    );
  }

  @Action(FetchCurrencies)
  fetchCurrencies({ patchState }: StateContext<IssuerStoreModel>): Observable<ControlItem[]> {
    return this.http.get<ControlItem[]>(`service/currencies`).pipe(
      tap(currencies => patchState({ currencies }))
    );
  }

  @Action(UpdateIssuerIsPreviouslyPriced)
  updateIssuerIsPreviouslyPriced({ getState, patchState }: StateContext<IssuerStoreModel>): void {
    this.http.get<number[]>('service/issuers/priced').subscribe(previouslyPricedIssuerIds => {
      const issuers = getState().issuers;
      if (issuers.length) {
        issuers.forEach(issuer => issuer.isPreviouslyPriced = previouslyPricedIssuerIds.includes(issuer.issuerId));
        patchState({ issuers });
      }
    });
  }

  @Action(FetchSelectedIssuerCurrencies)
  fetchSelectedIssuerCurrencies({ patchState }: StateContext<IssuerStoreModel>, { issuerId }: FetchSelectedIssuerCurrencies): void {
    if (issuerId === null || issuerId === undefined) {
      patchState({ selectedIssuerCurrencies: [] });
    } else {
      this.http.get<ControlItem[]>(`service/issuer/${issuerId}/currencies`).subscribe(currencies => patchState({ selectedIssuerCurrencies: currencies }));
    }
  }
}
